import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import {
  Search, ChevronRight, X, Star, BookOpen, Users, Video,
  GraduationCap, Filter, MessageCircle, ArrowRight
} from 'lucide-react';

function AllTeachers() {
  const [searchParams] = useSearchParams();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState(searchParams.get('subject') || '');
  const [sortBy, setSortBy] = useState('popular');
  const [billingEnabled, setBillingEnabled] = useState(true);

  useEffect(() => {
    axios.get(`${API}/teachers`)
      .then(res => { setTeachers(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/settings`)
      .then(res => {
        const s = res.data || {};
        setBillingEnabled(s.billingEnabledWeb !== false && s.billingEnabled !== false);
      })
      .catch(() => {});
  }, []);

  const subjects = useMemo(() =>
    [...new Set(teachers.flatMap(t => (t.subjects || [t.subject]).filter(Boolean)))]
  , [teachers]);

  const filteredTeachers = useMemo(() => {
    let result = [...teachers];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.name?.toLowerCase().includes(q) ||
        t.subject?.toLowerCase().includes(q) ||
        t.bio?.toLowerCase().includes(q)
      );
    }
    if (selectedSubject) {
      result = result.filter(t =>
        t.subject === selectedSubject ||
        (t.subjects || []).includes(selectedSubject)
      );
    }
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => (b.total_students || 0) - (a.total_students || 0));
        break;
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default: break;
    }
    return result;
  }, [teachers, searchQuery, selectedSubject, sortBy]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="all-teachers-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-10">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Our Teachers</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2" data-testid="teachers-page-title">Meet Our Teachers</h1>
          <p className="text-white/80 mb-6">Learn from {teachers.length} experienced educators</p>
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" placeholder="Search by name, subject..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-slate-900 bg-white rounded-xl border-0 shadow-lg focus:ring-2 focus:ring-white/50"
              data-testid="teacher-search-input" />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2">
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-md sticky top-16 z-40" data-testid="teachers-filter-sticky-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2 filter-pills-row flex-1 min-w-0">
              <button onClick={() => setSelectedSubject('')}
                className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
                  !selectedSubject ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`} data-testid="filter-all">All</button>
              {subjects.map(s => (
                <button key={s} onClick={() => setSelectedSubject(s)}
                  className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition-all ${
                    selectedSubject === s ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`} data-testid={`filter-${s.toLowerCase()}`}>{s}</button>
              ))}
            </div>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500"
              data-testid="sort-select">
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="name">A-Z</option>
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <p className="text-sm text-slate-500 mb-6">{filteredTeachers.length} teachers found</p>

        {filteredTeachers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-5 gap-y-8">
            {filteredTeachers.map(teacher => {
              const isEngaged = teacher.is_live_teaching || teacher.availability_status === 'engaged';
              const isAvailable = !isEngaged && (teacher.is_online || teacher.availability_status === 'available');
              return (
                <Link
                  key={teacher.id}
                  to={`/teachers/${teacher.id}`}
                  className="group text-center"
                  data-testid={`teacher-card-${teacher.id}`}
                >
                  {/* Round avatar with status dot — same treatment as Home */}
                  <div className="relative w-24 h-24 mx-auto mb-3">
                    {teacher.avatar ? (
                      <img
                        loading="lazy"
                        src={teacher.avatar}
                        alt={teacher.name}
                        className="w-24 h-24 rounded-full object-cover shadow-lg group-hover:shadow-xl transition-shadow"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                        <span className="text-2xl font-black text-indigo-600">
                          {teacher.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {/* Online status indicator dot */}
                    <div className="absolute bottom-1 right-1" data-testid={`teacher-status-${teacher.id}`}>
                      {isEngaged ? (
                        <div className="w-5 h-5 bg-red-500 rounded-full border-[3px] border-white shadow-md" title="Engaged" />
                      ) : isAvailable ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full border-[3px] border-white shadow-md" title="Available" />
                      ) : (
                        <div className="w-5 h-5 bg-gray-400 rounded-full border-[3px] border-white shadow-md" title="Away" />
                      )}
                    </div>
                    {/* Rating chip */}
                    {teacher.rating > 0 && (
                      <div className="absolute -top-1 -right-1 bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 shadow">
                        <Star className="w-2.5 h-2.5 fill-current" /> {teacher.rating.toFixed(1)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-sm group-hover:text-teal-600 transition-colors line-clamp-1">
                    {teacher.name}
                  </h3>
                  <p className="text-xs text-slate-500 mb-0.5 line-clamp-1">
                    {(teacher.subjects || [teacher.subject]).filter(Boolean).slice(0, 2).join(', ')}
                  </p>
                  {teacher.preferred_levels?.length > 0 && (
                    <p className="text-[10px] text-teal-600 font-medium mb-0.5 line-clamp-1">
                      {teacher.preferred_levels.slice(0, 3).join(', ')}
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {teacher.followers || teacher.total_students || 0}
                    </span>
                    {isEngaged && <span className="text-red-500 font-semibold">Engaged</span>}
                    {isAvailable && <span className="text-green-500 font-semibold">Available</span>}
                  </div>
                  {billingEnabled && teacher.rate_per_hour > 0 && (
                    <p className="text-xs text-teal-600 font-bold mt-1">
                      TSh {teacher.rate_per_hour.toLocaleString()}/hr
                    </p>
                  )}
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Teachers Found</h3>
            <p className="text-slate-500 mb-4">Try adjusting your search</p>
            <Button onClick={() => { setSearchQuery(''); setSelectedSubject(''); }} variant="outline">Clear Filters</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllTeachers;
